import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FiArrowRight, FiDownload } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { formatCurrency } from '../utils/currencyUtils';
import { exportToCSV } from '../utils/exportCSV';
import CustomPagination from './CustomPagination/CustomPagination';
import SkeletonTable from './common/SkeletonTable';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const TYPE_META = {
  school_fee:           { label: 'School Fee',   bg: 'bg-indigo-100', text: 'text-indigo-700' },
  discount_settlement:  { label: 'Settlement',   bg: 'bg-green-100',  text: 'text-green-700'  },
  withdrawal:           { label: 'Withdrawal',   bg: 'bg-red-100',    text: 'text-red-700'    },
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_META[type] || { label: type || 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${meta.bg} ${meta.text}`}>
      {meta.label}
    </span>
  );
};

const transactionTypes = [
  { id: 'all',                  label: 'All'         },
  { id: 'school_fee',           label: 'School Fees' },
  { id: 'discount_settlement',  label: 'Settlements' },
  { id: 'withdrawal',           label: 'Withdrawals' },
];

const getLink = (row) => {
  if (row.type === 'withdrawal')          return `/expense-details/${row._id}`;
  if (row.type === 'discount_settlement') return `/settlement-details/${row._id}`;
  return `/transaction-details/${row._id}`;
};

const getParent = (row) => {
  if (row.type === 'discount_settlement') {
    return row.metadata?.parent
      ? `${row.metadata.parent.firstname || ''} ${row.metadata.parent.lastname || ''}`.trim()
      : '—';
  }
  if (row.type === 'withdrawal') return '—';
  const name = row.metadata?.parent_name;
  return name && name !== 'undefined undefined' ? name : '—';
};

const getStudent = (row) => {
  if (row.type === 'withdrawal' || row.type === 'discount_settlement') return '—';
  return row.child ? `${row.child.firstname} ${row.child.lastname}` : '—';
};

const TransactionTable = ({
  transactions,
  loading,
  onTransactionTypeChange,
  activeTransactionType = 'all',
}) => {
  const [filterText, setFilterText] = useState('');
  const [paginationState, setPaginationState] = useState(() => {
    const saved = localStorage.getItem('transactionsPagination');
    return saved ? JSON.parse(saved) : { rowsPerPage: 10, currentPage: 1 };
  });

  useEffect(() => {
    const saved = localStorage.getItem('transactionsFilterText');
    if (saved) setFilterText(saved);
  }, []);

  useEffect(() => {
    localStorage.setItem('transactionsFilterText', filterText);
  }, [filterText]);

  // Count per tab
  const counts = transactionTypes.reduce((acc, t) => {
    acc[t.id] = t.id === 'all'
      ? (transactions?.length || 0)
      : (transactions?.filter(tx => tx.type === t.id).length || 0);
    return acc;
  }, {});

  const columns = [
    {
      name: 'Type',
      cell: row => <TypeBadge type={row.type} />,
      exportCell: row => TYPE_META[row.type]?.label || row.type,
      width: '130px',
    },
    {
      name: 'Parent',
      cell: row => <span className="text-sm text-gray-700">{getParent(row)}</span>,
      exportCell: row => getParent(row),
      minWidth: '140px',
    },
    {
      name: 'Student',
      cell: row => <span className="text-sm text-gray-700">{getStudent(row)}</span>,
      exportCell: row => getStudent(row),
      minWidth: '140px',
    },
    {
      name: 'Class',
      cell: row => <span className="text-sm text-gray-500">{row.metadata?.class?.class_name || '—'}</span>,
      exportCell: row => row.metadata?.class?.class_name || '—',
      hide: 'md',
      minWidth: '100px',
    },
    {
      name: 'Amount Paid',
      cell: row => (
        <span className={`font-semibold text-sm
          ${row.type === 'discount_settlement' ? 'text-green-600' : row.type === 'withdrawal' ? 'text-red-600' : 'text-gray-800'}`}
        >
          {formatCurrency(row.amount)}
        </span>
      ),
      exportCell: row => formatCurrency(row.amount),
      minWidth: '120px',
    },
    {
      name: 'Balance',
      cell: row => {
        const bal = Number(row.total_amount || 0) - Number(row.amount_after || 0);
        return (
          <span className={`font-medium text-sm ${bal > 0 ? 'text-red-500' : 'text-green-600'}`}>
            {formatCurrency(bal)}
          </span>
        );
      },
      exportCell: row => formatCurrency(Number(row.total_amount || 0) - Number(row.amount_after || 0)),
      hide: 'sm',
      minWidth: '110px',
    },
    {
      name: 'Date',
      cell: row => (
        <span className="text-xs text-gray-500">
          {new Date(row.updatedAt).toLocaleString('en-NG', { hour12: true, dateStyle: 'medium', timeStyle: 'short' })}
        </span>
      ),
      sortable: true,
      hide: 'md',
      minWidth: '160px',
    },
    {
      cell: row => (
        <Link to={getLink(row)}>
          <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
            <FiArrowRight className="w-4 h-4" />
          </button>
        </Link>
      ),
      ignoreRowClick: true,
      button: true,
      width: '56px',
    },
  ];

  const filteredData = transactions?.filter(item => {
    const q = filterText.toLowerCase();
    return (
      item.child?.firstname?.toLowerCase().includes(q) ||
      item.child?.lastname?.toLowerCase().includes(q) ||
      item.metadata?.parent_name?.toLowerCase().includes(q) ||
      item.metadata?.class?.class_name?.toLowerCase().includes(q) ||
      item._id?.toLowerCase().includes(q)
    );
  }) || [];

  const currentDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
  };

  return (
    <div className="w-full">
      {/* Tab strip */}
      <div className="flex gap-2 overflow-x-auto pb-1 mb-5 scrollbar-hide">
        {transactionTypes.map(t => {
          const active = activeTransactionType === t.id;
          return (
            <button
              key={t.id}
              onClick={() => onTransactionTypeChange(t.id)}
              className={`flex items-center gap-2 shrink-0 py-2 px-4 rounded-full text-sm font-medium transition-all
                ${active
                  ? 'bg-[#3F3DA5] text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-indigo-300 hover:text-indigo-600'
                }`}
            >
              {t.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold
                ${active ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}
              >
                {counts[t.id]}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search + count + export */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <MdSearch className="text-gray-400" />
            </div>
            <input
              type="text"
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Search..."
              className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-48 md:w-64"
            />
          </div>
          <span className="text-sm text-gray-400 hidden sm:block">
            {filteredData.length} result{filteredData.length !== 1 ? 's' : ''}
          </span>
        </div>

        <button
          onClick={() => exportToCSV(filteredData, columns, `transactions-${currentDate()}.csv`)}
          className="inline-flex items-center gap-2 py-2 px-4 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-full transition-colors"
        >
          <FiDownload className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      {/* Table */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredData}
          getRowId={row => row._id}
          pagination
          paginationDefaultPage={paginationState.currentPage}
          paginationPerPage={paginationState.rowsPerPage}
          onChangePage={page => {
            const s = { ...paginationState, currentPage: page };
            setPaginationState(s);
            localStorage.setItem('transactionsPagination', JSON.stringify(s));
          }}
          onChangeRowsPerPage={(perPage, page) => {
            const s = { rowsPerPage: perPage, currentPage: page };
            setPaginationState(s);
            localStorage.setItem('transactionsPagination', JSON.stringify(s));
          }}
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={6} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">
              No transactions found for the selected filters.
            </div>
          }
        />
      </div>
    </div>
  );
};

export default TransactionTable;
